import { useState, useRef, useEffect } from "react";
import whatsappIcon from "@/assets/whatsapp.png";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SupportHubProps {
  onOpenChat: () => void;
  isChatOpen: boolean;
}

export function SupportHub({ onOpenChat, isChatOpen }: SupportHubProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Hide hub entirely when chat is open
  if (isChatOpen) return null;

  const handleWhatsApp = () => {
    window.open(
      "https://wa.me/5581986412888?text=Olá! Preciso de ajuda com o ProntuÁudio.",
      "_blank"
    );
    setIsExpanded(false);
  };

  const handleOpenPronty = () => {
    setIsExpanded(false);
    onOpenChat();
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Expanded popover */}
      {isExpanded && (
        <div
          ref={popoverRef}
          className={cn(
            "w-[320px] max-w-[calc(100vw-48px)] bg-background border rounded-2xl shadow-2xl p-5",
            "animate-in fade-in slide-in-from-bottom-4 duration-200"
          )}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              Como podemos ajudar?
            </h3>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            Você pode entrar em contato diretamente com nosso chefe de
            desenvolvimento ou tirar suas dúvidas rapidamente com a Pronty.
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 text-foreground transition-colors text-sm font-medium"
            >
              <img src={whatsappIcon} alt="WhatsApp" className="w-6 h-6" />
              Falar pelo WhatsApp
            </button>
            <button
              onClick={handleOpenPronty}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-primary/10 hover:bg-primary/20 text-foreground transition-colors text-sm font-medium"
            >
              <MessageCircle className="w-5 h-5 text-primary" />
              Conversar com a Pronty
            </button>
          </div>
        </div>
      )}

      {/* Balloon + Button row */}
      <div className="flex items-center gap-3">
        {/* Dismissible balloon */}
        {!dismissed && !isExpanded && (
          <div className="relative bg-background border rounded-xl shadow-lg px-3 py-2 text-xs text-foreground font-medium max-w-[200px] animate-in fade-in slide-in-from-right-4 duration-300">
            Fale diretamente com o desenvolvedor
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDismissed(true);
              }}
              className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-muted rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-2.5 h-2.5" />
            </button>
            {/* Arrow pointing right */}
            <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-background border-r border-b rotate-[-45deg]" />
          </div>
        )}

        {/* WhatsApp icon button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 overflow-hidden flex-shrink-0"
          aria-label="Abrir opções de suporte"
        >
          <img
            src={whatsappIcon}
            alt="Suporte"
            className="w-full h-full object-cover"
          />
        </button>
      </div>
    </div>
  );
}
